import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { ArrowLeft, ArrowRight, Search, Loader2, ExternalLink, Check, X, Sparkles, Download, Heart } from "lucide-react";

/**
 * Hugging Face Import Wizard — 4 steps
 *   1. Search & pick a model
 *   2. Provide HF token + verify
 *   3. Configure (alias, cost, context)
 *   4. Save
 */
export default function StudioHFImport() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [q, setQ] = useState("llama");
  const [task, setTask] = useState("text-generation");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [card, setCard] = useState(null);
  const [token, setToken] = useState("");
  const [provider, setProvider] = useState("");
  const [verifyState, setVerifyState] = useState({ status: "idle", msg: "" });
  const [form, setForm] = useState({
    name: "",
    avatar: "🤗",
    description: "",
    context: 32000,
    cost_in: 0,
    cost_out: 0,
    tags: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { runSearch(); /* initial */ }, []); // eslint-disable-line

  /** Detect and auto-pick if user pasted a HF URL or org/repo path. */
  const tryUrlAutoPick = async (raw) => {
    const s = (raw || "").trim();
    // Match "huggingface.co/org/repo" or "org/repo" (with optional trailing slash/hash)
    const m = s.match(/(?:huggingface\.co\/)?([\w\-.]+\/[\w\-.]+)/);
    if (!m) return false;
    const repo = m[1].replace(/[\/]+$/, "");
    // Only auto-pick if it looks like a real repo (contains a slash and no spaces)
    if (!repo.includes("/") || /\s/.test(repo)) return false;
    try {
      const r = await api.get(`/hf/model/${repo}`);
      // Success — treat as picked
      pick({
        repo_id: r.data.repo_id || repo,
        author: r.data.author || repo.split("/")[0],
        downloads: r.data.downloads,
        likes: r.data.likes,
        tags: r.data.tags,
        license: r.data.license,
      });
      return true;
    } catch (e) {
      return false;
    }
  };

  const runSearch = async () => {
    // If the query looks like an HF URL / repo path, try to auto-pick first
    if (q && q.includes("/")) {
      const ok = await tryUrlAutoPick(q);
      if (ok) return;
    }
    setSearching(true);
    try {
      const r = await api.get(`/hf/search`, { params: { q, task, limit: 24 } });
      setResults(r.data.models || []);
    } catch (e) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const pick = async (m) => {
    setPicked(m);
    setForm((f) => ({
      ...f,
      name: m.repo_id.split("/").pop().replace(/-/g, " ").slice(0, 60),
    }));
    try {
      const r = await api.get(`/hf/model/${m.repo_id}`);
      setCard(r.data);
    } catch (e) {
      setCard(null);
    }
    setStep(2);
  };

  const verify = async () => {
    if (!token.trim()) return;
    setVerifyState({ status: "checking", msg: "Pinging Hugging Face…" });
    try {
      const r = await api.post("/hf/verify", { repo_id: picked.repo_id, hf_token: token.trim(), provider: provider.trim() });
      if (r.data.ok) {
        setVerifyState({ status: "ok", msg: `Model responded: "${(r.data.reply || "").trim().slice(0, 80)}"` });
      } else {
        setVerifyState({ status: "fail", msg: r.data.error || "Verification failed" });
      }
    } catch (e) {
      setVerifyState({ status: "fail", msg: e.response?.data?.detail || e.message });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/ai-models/hf-import", {
        name: form.name,
        avatar: form.avatar,
        description: form.description,
        hf_repo_id: picked.repo_id,
        hf_token: token,
        hf_provider: provider,
        context: form.context,
        cost_in: form.cost_in,
        cost_out: form.cost_out,
        license: card?.license || "",
        tags: form.tags,
      });
      nav(`/studio/${r.data.ai_model_id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const stepPill = (n, label) => (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border-2 font-heading font-bold text-xs uppercase tracking-widest ${step === n ? "bg-slate-900 text-white border-slate-900" : n < step ? "bg-amber-200 border-slate-900 text-slate-900" : "bg-white border-slate-300 text-slate-500"}`}>
      {n < step ? <Check size={12} strokeWidth={3} /> : <span className="w-4 text-center">{n}</span>}
      {label}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/studio" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm font-bold" data-testid="back-to-studio">
          <ArrowLeft size={14} strokeWidth={2.5} /> Studio
        </Link>
      </div>
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          🤗 Hugging Face Import
        </div>
        <h1 className="font-heading text-4xl font-bold" data-testid="hf-title">Bring in any HF model</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Search the Hub, pick a model, paste your HF token — we wrap it in an OpenAI-compatible brain that works everywhere on AiMedley.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="hf-steps">
        {stepPill(1, "Pick model")}
        {stepPill(2, "Token & verify")}
        {stepPill(3, "Configure")}
        {stepPill(4, "Save")}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="brut-card p-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">Search or paste HF URL</div>
                <input className="brut-input w-full" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="llama, mistral, or paste 'org/repo' or 'huggingface.co/org/repo'…" data-testid="hf-search-input" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">Task</div>
                <select className="brut-input" value={task} onChange={(e) => setTask(e.target.value)} data-testid="hf-task">
                  <option value="text-generation">Text generation</option>
                  <option value="conversational">Conversational</option>
                  <option value="text2text-generation">Text2text</option>
                </select>
              </div>
              <button onClick={runSearch} disabled={searching} className="brut-btn text-sm" data-testid="hf-search-btn">
                {searching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} strokeWidth={3} />} Search
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="hf-results">
            {results.map((m) => (
              <button
                key={m.repo_id}
                onClick={() => pick(m)}
                className="brut-card p-3 text-left hover:-translate-y-0.5 transition-transform"
                data-testid={`hf-result-${m.repo_id.replace(/[\/\.]/g, "_")}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm truncate">{m.repo_id}</div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{m.author}</div>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 shrink-0 flex items-center gap-2">
                    <span className="inline-flex items-center gap-0.5"><Download size={9} /> {formatNum(m.downloads)}</span>
                    <span className="inline-flex items-center gap-0.5"><Heart size={9} /> {formatNum(m.likes)}</span>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(m.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 truncate max-w-[110px]">{t}</span>
                  ))}
                  {m.license && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800">{m.license}</span>}
                </div>
              </button>
            ))}
            {!searching && results.length === 0 && <div className="italic text-slate-400 col-span-2 text-sm">No models found. Try a different query.</div>}
          </div>
        </div>
      )}

      {step === 2 && picked && (
        <div className="space-y-4">
          <div className="brut-card p-4">
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500 mb-1">Picked model</div>
            <div className="font-heading font-bold text-lg">{picked.repo_id}</div>
            {card && (
              <div className="text-xs text-slate-600 mt-2 flex flex-wrap gap-2">
                {card.license && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800">License: {card.license}</span>}
                {card.gated && <span className="px-1.5 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-700">⚠ Gated — requires HF access approval</span>}
                <a href={`https://huggingface.co/${picked.repo_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">View on Hub <ExternalLink size={10} /></a>
              </div>
            )}
          </div>
          <div className="brut-card p-4 space-y-3">
            <Field label="Hugging Face Access Token" required>
              <input className="brut-input w-full font-mono" type="password" placeholder="hf_..." value={token} onChange={(e) => setToken(e.target.value)} data-testid="hf-token" />
              <p className="text-xs text-slate-500 mt-1">Get one at <a className="underline" href="https://huggingface.co/settings/tokens" target="_blank" rel="noreferrer">huggingface.co/settings/tokens</a> — must include <b>"Inference Providers"</b> scope. Stored encrypted per user.</p>
            </Field>
            <Field label="Inference provider (optional)">
              <input className="brut-input w-full" placeholder="Leave blank for auto — or try 'groq', 'together', 'fireworks-ai'" value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="hf-provider" />
            </Field>
            <button onClick={verify} disabled={!token.trim() || verifyState.status === "checking"} className="brut-btn text-sm" data-testid="hf-verify">
              {verifyState.status === "checking" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={3} />} Verify token
            </button>
            {verifyState.status !== "idle" && (
              <div className={`text-sm px-3 py-2 rounded-lg border-2 ${verifyState.status === "ok" ? "bg-green-50 border-green-300 text-green-800" : verifyState.status === "fail" ? "bg-red-50 border-red-300 text-red-800" : "bg-slate-50 border-slate-200 text-slate-600"}`} data-testid="hf-verify-result">
                {verifyState.status === "ok" ? <><Check size={12} className="inline mr-1" strokeWidth={3} />{verifyState.msg}</> : verifyState.status === "fail" ? <><X size={12} className="inline mr-1" strokeWidth={3} />{verifyState.msg}</> : verifyState.msg}
              </div>
            )}
          </div>
        </div>
      )}

      {step === 3 && picked && (
        <div className="brut-card p-4 space-y-4">
          <Field label="Display name" required>
            <input className="brut-input w-full" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="hf-name" />
          </Field>
          <div className="grid grid-cols-[100px_1fr] gap-4">
            <Field label="Avatar">
              <input className="brut-input w-full text-3xl text-center" maxLength={4} value={form.avatar} onChange={(e) => setForm((f) => ({ ...f, avatar: e.target.value }))} data-testid="hf-avatar" />
            </Field>
            <Field label="Description">
              <input className="brut-input w-full" placeholder="A one-liner describing the model." value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="hf-description" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Context length">
              <input className="brut-input w-full" type="number" value={form.context} onChange={(e) => setForm((f) => ({ ...f, context: parseInt(e.target.value || "0") }))} data-testid="hf-context" />
            </Field>
            <Field label="Cost in ($/1M tok)">
              <input className="brut-input w-full" type="number" step="0.01" value={form.cost_in} onChange={(e) => setForm((f) => ({ ...f, cost_in: parseFloat(e.target.value || "0") }))} data-testid="hf-cost-in" />
            </Field>
            <Field label="Cost out ($/1M tok)">
              <input className="brut-input w-full" type="number" step="0.01" value={form.cost_out} onChange={(e) => setForm((f) => ({ ...f, cost_out: parseFloat(e.target.value || "0") }))} data-testid="hf-cost-out" />
            </Field>
          </div>
        </div>
      )}

      {step === 4 && picked && (
        <div className="brut-card p-6 space-y-3 text-center">
          <div className="text-4xl">🤗</div>
          <div className="font-heading text-2xl font-bold">Ready to import</div>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            <span className="font-bold">{form.name}</span> ({picked.repo_id}) will be available across Compare, Debate, Workflows, and Workers as a brand-new brain.
          </p>
          <button onClick={save} disabled={saving} className="brut-btn text-sm" data-testid="hf-save">
            <Sparkles size={12} strokeWidth={3} /> {saving ? "Importing…" : "Import model"}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          className="brut-btn text-sm bg-white text-slate-900 disabled:opacity-40"
          data-testid="hf-prev"
        >
          <ArrowLeft size={12} strokeWidth={3} /> Back
        </button>
        {step < 4 && (
          <button
            onClick={() => setStep(step + 1)}
            disabled={(step === 1 && !picked) || (step === 2 && verifyState.status !== "ok") || (step === 3 && !form.name.trim())}
            className="brut-btn text-sm disabled:opacity-40"
            data-testid="hf-next"
          >
            Next <ArrowRight size={12} strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">
        {label}{required && <span className="text-red-500">*</span>}
      </div>
      {children}
    </div>
  );
}

function formatNum(n) {
  if (!n) return "0";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return `${n}`;
}
